import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApplicationFilterState } from "../components/ApplicationsFilter/ApplicationsFilter";
import { ErrorToast, SuccessToast } from "../components/toastMessage";
import { _axios } from "../utils/axios";
import {
  TApplicationField,
  TApplicationFieldOption,
} from "./application-fields.hooks";
import { TDocumentConfig, TForm } from "./forms.hooks";
import { TLevel2PaymentReceipt } from "./level-2-payment-approval.hooks";

export type TApplication = {
  id: number;
  userId: number;
  status: string;
  createdAt: string;
  updatedAt: string;
  submittedAt?: string;
  applicationId: string;
  project_name?: string;
  plot_owner_s_name?: string;
  is_there_a_construction?: boolean;
};

export const applicationsKeyStore = {
  declaration: ["declaration"],
  nameOfService: ["name-of-service"],
  allFields: (type: string) => ["application", "all-fields", type],
  basicForm: ["base-form"],
  generatedDocs: (...id: (number | string)[]) => ["generated-docs", ...id],
  receipts: (...id: (number | string)[]) => ["application-receipts", ...id],
  basic: (arg: number | string) => ["basic-application-info", arg],
  id: (...arg: (number | string)[]) => ["application", ...arg],
  counts: (...args: (number | string | Record<string, unknown>)[]) => [
    "applications-counts",
    ...args,
  ],
  list: (...args: (number | string | Record<string, unknown>)[]) => [
    "applications",
    ...args,
  ],
  stats: (...args: (number | string | Record<string, unknown>)[]) => [
    "applications-stats",
    ...args,
  ],
  serviceFormSteps: (id: number | undefined) => ["service-form-steps", id],
  filterFields: ["application-filter-fields"],
  condition: (arg: number | string) => ["check-existing-record", arg],
  certificateCondition: (arg: number | string) => [
    "check-existing-record",
    arg,
  ],
};

export function useGeneratedDocs({
  id,
  enabled = true,
}: {
  id: string | number;
  enabled?: boolean;
}) {
  return useQuery({
    queryKey: applicationsKeyStore.generatedDocs(id),
    queryFn: () =>
      _axios.get<{
        success: true;
        data: { type: string; url: string; id: number }[];
      }>("/applications/generated-documents", {
        params: { applicationId: id },
      }),
    select: (res) => res?.data?.data,
    staleTime: 5 * 60 * 1000,
    enabled: !!id && enabled,
  });
}

export function useGeneratedDocsAdmin({
  id,
  enabled = true,
}: {
  id: string | number;
  enabled?: boolean;
}) {
  return useQuery({
    queryKey: applicationsKeyStore.generatedDocs(id, "admin"),
    queryFn: () =>
      _axios.get<{
        success: true;
        data: { type: string; url: string; id: number }[];
      }>("/applications/generated-documents-admin", {
        params: { applicationId: id },
      }),
    select: (res) => res?.data?.data,
    staleTime: 5 * 60 * 1000,
    enabled: !!id && enabled,
  });
}

export function usePaymentReceipts({
  id,
  enabled = true,
}: {
  id: string | number;
  enabled?: boolean;
}) {
  return useQuery({
    queryKey: applicationsKeyStore.receipts(id),
    queryFn: () =>
      _axios.get<{
        success: true;
        data: TLevel2PaymentReceipt[];
      }>(`/applications/receipts/${id}`),
    select: (res) => res?.data?.data,
    staleTime: 5 * 60 * 1000,
    enabled: !!id && enabled,
  });
}

export function usePaymentReceiptsAdmin({
  id,
  enabled = true,
}: {
  id: string | number;
  enabled?: boolean;
}) {
  return useQuery({
    queryKey: applicationsKeyStore.receipts(id, "admin"),
    queryFn: () =>
      _axios.get<{
        success: true;
        data: TLevel2PaymentReceipt[];
      }>(`/applications/receipts/admin/${id}`),
    select: (res) => res?.data?.data,
    staleTime: 5 * 60 * 1000,
    enabled: !!id && enabled,
  });
}

export function useApplicationStats({
  filters,
}: {
  filters?: ApplicationFilterState;
}) {
  return useQuery({
    queryKey: applicationsKeyStore.stats(filters || {}),
    queryFn: () =>
      _axios.post<{
        success: true;
        hasMore?: boolean;
        data: { status: string; count: number }[];
      }>("/applications/stats", { filters }),
    staleTime: 10 * 60 * 1000,
    select: (d) => d.data,
  });
}

export function useApplicationCounts({
  search,
  filters,
}: {
  search?: string;
  filters?: ApplicationFilterState;
}) {
  return useQuery({
    queryKey: applicationsKeyStore.counts(search || "", filters || {}),
    queryFn: () =>
      _axios.post<{
        success: true;
        hasMore?: boolean;
        data: number;
      }>("/applications/counts", { search, filters }),
    staleTime: 10 * 60 * 1000,
    select: (d) => d.data,
  });
}

export function useApplications({
  page,
  limit,
  search,
  sort,
  sortVal,
  filters,
}: {
  page: number;
  limit: number;
  search?: string;
  sort: string;
  sortVal: number;
  filters?: ApplicationFilterState;
}) {
  return useQuery({
    queryKey: applicationsKeyStore.list(
      page,
      limit,
      sort,
      sortVal,
      search || "",
      filters || {}
    ),
    queryFn: () =>
      _axios.post<{
        success: true;
        hasMore?: boolean;
        data: (TApplication & { serviceName?: string })[];
      }>("/applications", {
        sort,
        sortVal,
        search,
        page,
        limit,
        filters: filters || {},
      }),
    staleTime: 10 * 60 * 1000,
    select: (d) => d.data,
  });
}

export type TPopulatedForm = TForm & {
  documentConfigs?: TDocumentConfig[];
  fields?: (TApplicationField & {
    options?: TApplicationFieldOption[];
  })[];
};

export function useBasicForm() {
  return useQuery({
    queryKey: applicationsKeyStore.basicForm,
    queryFn: () =>
      _axios.get<{
        success: true;
        hasMore?: boolean;
        data: TPopulatedForm;
      }>("/applications/basic-form"),
    staleTime: 10 * 60 * 1000,
    select: (d) => d.data,
  });
}

export function useApplicationFormSteps(serviceOptionId: number | undefined) {
  return useQuery({
    queryKey: applicationsKeyStore.serviceFormSteps(serviceOptionId),
    queryFn: () =>
      _axios.get<{
        success: true;
        hasMore?: boolean;
        data: TPopulatedForm[];
      }>("/applications/steps", { params: { serviceOptionId } }),
    enabled: typeof serviceOptionId === "number" && !isNaN(serviceOptionId),
    staleTime: 10 * 60 * 1000,
    select: (d) => d.data,
  });
}

export function useUploadFile() {
  return useMutation({
    mutationFn: (file: File) => {
      const data = new FormData();
      data.set("file", file);
      return _axios.post<{ success: boolean; data?: string }>(
        "/applications/upload-file",
        data
      );
    },
  });
}

export function useCreateDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { id?: number; data: Record<string, unknown> }) =>
      _axios.post<{ success: true; data: TApplication }>(
        "/applications/save-draft",
        data
      ),
    onSuccess: (res) => {
      if (res.data?.data?.applicationId) {
        SuccessToast("Application saved!");
      }
    },
    onError: () => {
      ErrorToast("Something went wrong while saving application!");
    },
    retry: 2,
    retryDelay: (count) => count * 1000,
    onSettled: () => {
      qc.invalidateQueries({ queryKey: applicationsKeyStore.list() });
      qc.invalidateQueries({ queryKey: applicationsKeyStore.counts() });
      qc.invalidateQueries({ queryKey: applicationsKeyStore.stats() });
    },
  });
}

export function useApplication({ id }: { id: number | string }) {
  return useQuery({
    queryKey: applicationsKeyStore.id(id),
    queryFn: () =>
      _axios.get<{
        success: true;
        data: TApplication & Record<string, unknown>;
      }>(`/applications/${id}`),
    select: (d) => d?.data?.data,
    enabled: !!id,
  });
}

export function useApplicationAdmin({ id }: { id: number | string }) {
  return useQuery({
    queryKey: applicationsKeyStore.id(id, "admin"),
    queryFn: () =>
      _axios.get<{
        success: true;
        data: TApplication & Record<string, unknown>;
      }>(`/applications/admin/${id}`),
    select: (d) => d?.data?.data,
    enabled: !!id,
  });
}

export function useDeclaration() {
  return useQuery({
    queryKey: applicationsKeyStore.declaration,
    queryFn: () =>
      _axios.get<{ success: boolean; data: string }>(
        "/applications/declaration"
      ),
    select: (d) => d?.data?.data,
  });
}

export function useSubmitApplication() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { id: number }) =>
      _axios.post<{ success: true; data: TApplication }>(
        "/applications/submit-application",
        data
      ),
    onSuccess: (res) => {
      if (res.data?.success) SuccessToast("Application submitted!");
    },
    onError: () => {
      ErrorToast("Something went wrong while submitting application!");
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: applicationsKeyStore.list() });
      qc.invalidateQueries({ queryKey: applicationsKeyStore.counts() });
      qc.invalidateQueries({ queryKey: applicationsKeyStore.stats() });
    },
  });
}

export function useFilterFields() {
  return useQuery({
    queryKey: applicationsKeyStore.filterFields,
    queryFn: () =>
      _axios.get<{
        success: true;
        data: (TApplicationField & { options?: TApplicationFieldOption[] })[];
      }>("/applications/filter-fields"),
    staleTime: 10 * 60 * 1000,
    select: (d) => d.data?.data || [],
  });
}

export function useApplicationForLandingPage(id: string) {
  return useQuery({
    enabled: !!id,
    queryKey: applicationsKeyStore.basic(id),
    queryFn: () =>
      _axios.get<{
        success: true;
        data: {
          applicationId: string;
          unauthorized_layout_name: string;
          mauja: string;
          khasara_no: string;
          area_of_plot: number;
          status: string;
        };
      }>(`/applications/get-basic-application-info`, { params: { id } }),
    select: (result) => result?.data,
  });
}

export function useNameOfServiceField() {
  return useQuery({
    queryKey: applicationsKeyStore.nameOfService,
    queryFn: () =>
      _axios.get<{
        success: true;
        data: TApplicationField & {
          options?: TApplicationFieldOption[];
        };
      }>(`/applications/name-of-service`),
    select: (result) => result?.data?.data,
    staleTime: Infinity,
  });
}

export function useServiceConditionField({ name_of_service }: any) {
  return useQuery({
    queryKey: applicationsKeyStore.condition(name_of_service),
    queryFn: () =>
      _axios.get<{
        success: true;
        data: any;
      }>(`/condition/check-existing-record`, { params: { name_of_service } }),
    select: (result) => result?.data?.data,
    staleTime: Infinity,
  });
}

export function useServiceCertificateConditionField({ name_of_service }: any) {
  return useQuery({
    queryKey: applicationsKeyStore.certificateCondition(name_of_service),
    queryFn: () =>
      _axios.get<{
        success: true;
        data: any;
      }>(`/certificate-condition/check-existing-record`, {
        params: { name_of_service },
      }),
    select: (result) => result?.data?.data,
    staleTime: Infinity,
  });
}

export function useDeleteApplication() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (applicationId: number) =>
      _axios.delete<{ success: true }>(`/applications/${applicationId}`),
    onError: () =>
      ErrorToast("Something went wrong while deleting application!"),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: applicationsKeyStore.list() });
      qc.invalidateQueries({ queryKey: applicationsKeyStore.counts() });
      qc.invalidateQueries({ queryKey: applicationsKeyStore.stats() });
    },
  });
}

export function useAllFields({ serviceName }: { serviceName: string }) {
  return useQuery({
    queryKey: applicationsKeyStore.allFields(serviceName),
    queryFn: () =>
      _axios.get<{
        success: true;
        data: { fields: TApplicationField[] };
      }>("/applications/all-fields", { params: { serviceName } }),
    staleTime: 2 * 60 * 1000,
    select: (result) => result.data?.data?.fields || [],
    enabled: !!serviceName,
  });
}
